'use client';

import { BodyStrong, MetaDefault, MonoSmall } from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Handle, Position, type Node, type NodeProps } from '@xyflow/react';
import { useTranslations } from 'next-intl';
import type { AiComponentDto } from '@terra/shared-contracts-ai-applications';
import { resolveAiComponentVerificationBadge } from '../utils/aiComponentVerification';
import {
  COMPOSITION_CARD_HEIGHT,
  COMPOSITION_CARD_WIDTH,
  type CompositionCardVisibility,
  type CompositionVisualKind,
} from '../utils/compositionGraphModel';
import {
  COMPOSITION_CARD_BORDER,
  COMPOSITION_HIDDEN_DOT,
  COMPOSITION_SELECTED_BORDER,
  COMPOSITION_SELECTED_SHADOW,
  COMPOSITION_SHOWN_DOT,
  COMPOSITION_VERIFIED_BORDER,
  COMPOSITION_VISUAL_STYLE,
} from '../utils/compositionGraphVisual';

export type CompositionNodeData = {
  component: AiComponentDto;
  visualKind: CompositionVisualKind;
  roleText: string;
  description: string | null;
  visibility: CompositionCardVisibility;
  verified: boolean;
  selected: boolean;
};

export type CompositionFlowNode = Node<CompositionNodeData, 'compositionNode'>;

export function CompositionNodeView({ data }: NodeProps<CompositionFlowNode>) {
  const tVerification = useTranslations('aiApplications.assets.verification');
  const tEvidence = useTranslations(
    'aiApplications.componentDrawer.verification',
  );
  const verificationBadge = resolveAiComponentVerificationBadge({
    component: data.component,
  });
  const visual = COMPOSITION_VISUAL_STYLE[data.visualKind];
  const Icon = visual.icon;
  const shown = data.visibility === 'shown' || data.visibility === 'verified';
  const status =
    data.visibility === 'shown'
      ? tEvidence('invocationUserVisible')
      : data.visibility === 'background'
        ? tEvidence('invocationHidden')
        : data.visibility === 'unknown'
          ? tEvidence('invocationUnknown')
          : data.visibility === 'verified'
            ? tVerification('verified')
            : null;
  const borderColor = data.selected
    ? COMPOSITION_SELECTED_BORDER
    : data.verified
      ? COMPOSITION_VERIFIED_BORDER
      : COMPOSITION_CARD_BORDER;

  return (
    <Box
      w={`${COMPOSITION_CARD_WIDTH}px`}
      h={`${COMPOSITION_CARD_HEIGHT}px`}
      boxSizing="border-box"
      bg="bg.default"
      borderWidth="1px"
      borderRadius="8px"
      p="12px"
      display="flex"
      flexDirection="column"
      gap="8px"
      cursor="pointer"
      boxShadow={data.selected ? undefined : 'sm'}
      style={{
        borderColor,
        boxShadow: data.selected ? COMPOSITION_SELECTED_SHADOW : undefined,
      }}
    >
      <Handle
        type="target"
        position={Position.Left}
        style={{ opacity: 0, pointerEvents: 'none' }}
      />
      <HStack gap="10px" minW={0} alignItems="center">
        <Box
          w="34px"
          h="34px"
          flexShrink={0}
          borderRadius="8px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          style={{
            background: visual.bg,
            color: visual.fg,
            boxShadow: `inset 0 0 0 1px ${visual.bd}`,
          }}
        >
          <Icon size={16} />
        </Box>
        <VStack gap="3px" minW={0} alignItems="flex-start">
          <BodyStrong lineClamp={1} lineHeight="1.2">
            {data.component.name}
          </BodyStrong>
          <MonoSmall
            lineClamp={1}
            lineHeight="1"
            letterSpacing="0.04em"
            style={{ color: visual.fg }}
          >
            {data.roleText}
          </MonoSmall>
        </VStack>
      </HStack>
      {data.description ? (
        <MetaDefault color="fg.default" lineClamp={2} lineHeight="1.45">
          {data.description}
        </MetaDefault>
      ) : null}
      <HStack gap="6px" mt="auto" minW={0} alignItems="flex-start">
        <MonoSmall
          flexShrink={0}
          px="6px"
          py="2px"
          borderRadius="4px"
          bg="bg.neutral.subtle"
          color="fg.default"
        >
          {tVerification(verificationBadge)}
        </MonoSmall>
        <Box
          w="6px"
          h="6px"
          mt="5px"
          flexShrink={0}
          borderRadius="full"
          style={{
            background: shown ? COMPOSITION_SHOWN_DOT : COMPOSITION_HIDDEN_DOT,
          }}
        />
        {status ? (
          <MetaDefault lineClamp={2} lineHeight="1.3" color="fg.muted">
            {status}
          </MetaDefault>
        ) : null}
      </HStack>
      <Handle
        type="source"
        position={Position.Right}
        style={{ opacity: 0, pointerEvents: 'none' }}
      />
    </Box>
  );
}
